
import React, { useEffect, useState } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Badge,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Input,
    Label,
    FormFeedback,
    FormGroup,
} from "reactstrap"
import { connect } from "react-redux"
import { useFormik } from "formik"
import * as Yup from "yup"

//Import Action
import {
    setBreadcrumbItems,
    getRoles,
    addNewRole,
    updateRole,
    deleteRole,
    getPermissions
} from "../../../store/actions"

const RolesList = (props) => {
    document.title = "Gestion des Rôles | CRM Admin";

    const [modal, setModal] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const [currentRole, setCurrentRole] = useState(null)

    const breadcrumbItems = [
        { title: "CRM", link: "#" },
        { title: "Admin", link: "#" },
        { title: "Rôles & Permissions", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Rôles & Permissions", breadcrumbItems)
        props.getRoles()
        props.getPermissions()
    }, [])

    const toggle = () => {
        if (modal) {
            setModal(false)
            setCurrentRole(null)
            setIsEdit(false)
        } else {
            setModal(true)
        }
    }

    const handleRoleClick = arg => {
        const role = arg
        setCurrentRole({
            id: role.id,
            name: role.name,
            description: role.description || "",
            level: role.level,
            type: role.type,
            permissions: role.permissions ? role.permissions.map(p => p.id) : [],
        })
        setIsEdit(true)
        toggle()
    }

    const handleDeleteRole = roleId => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer ce rôle ?")) {
            props.deleteRole(roleId)
        }
    }

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: (currentRole && currentRole.name) || "",
            description: (currentRole && currentRole.description) || "",
            level: (currentRole && currentRole.level) || 0,
            type: (currentRole && currentRole.type) || "company",
            permissions: (currentRole && currentRole.permissions) || [],
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Nom du rôle requis"),
            level: Yup.number().required("Niveau requis"),
            type: Yup.string().required("Type requis"),
        }),
        onSubmit: values => {
            if (isEdit) {
                const updateRoleData = {
                    id: currentRole.id,
                    ...values,
                }
                props.updateRole(updateRoleData)
            } else {
                props.addNewRole(values)
            }
            toggle()
        },
    })

    const handlePermissionChange = (permissionId) => {
        const { permissions } = validation.values;
        if (permissions.includes(permissionId)) {
            validation.setFieldValue("permissions", permissions.filter(id => id !== permissionId));
        } else {
            validation.setFieldValue("permissions", [...permissions, permissionId]);
        }
    };

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <CardTitle className="h4 mb-0">Gestion des Rôles et Permissions</CardTitle>
                                <Button color="primary" onClick={toggle}>
                                    <i className="mdi mdi-plus me-1"></i> Créer un Rôle
                                </Button>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-centered table-nowrap mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Nom du Rôle</th>
                                            <th>Type</th>
                                            <th>Niveau</th>
                                            <th>Permissions</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(props.roles || []).map((role, key) => (
                                            <tr key={key}>
                                                <td>
                                                    <div className="font-size-14 mb-1">{role.name}</div>
                                                    <div className="text-muted font-size-12">{role.description}</div>
                                                </td>
                                                <td>
                                                    {role.type === 'system' ? (
                                                        <span className="badge badge-soft-danger font-size-12 px-2 py-1">
                                                            Système
                                                        </span>
                                                    ) : (
                                                        <span className="badge badge-soft-primary font-size-12 px-2 py-1">
                                                            Entreprise
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{role.level}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <i className="mdi mdi-shield-check-outline text-success me-1"></i>
                                                        <span className="text-muted">{role.permissions_count || 0} actives</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            onClick={() => handleRoleClick(role)}
                                                        >
                                                            <i className="mdi mdi-pencil"></i>
                                                        </Button>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteRole(role.id)}
                                                        >
                                                            <i className="mdi mdi-trash-can"></i>
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={modal} toggle={toggle} size="xl">
                <ModalHeader toggle={toggle} tag="h4">
                    {!!isEdit ? "Modifier le Rôle" : "Créer un Rôle"}
                </ModalHeader>
                <ModalBody>
                    <Form
                        onSubmit={e => {
                            e.preventDefault()
                            validation.handleSubmit()
                            return false
                        }}
                    >
                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Nom du Rôle</Label>
                                    <Input
                                        name="name"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.name || ""}
                                        invalid={validation.touched.name && validation.errors.name ? true : false}
                                    />
                                    {validation.touched.name && validation.errors.name ? (
                                        <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                                    ) : null}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Type</Label>
                                    <Input
                                        name="type"
                                        type="select"
                                        className="form-select"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.type || "company"}
                                    >
                                        <option value="company">Entreprise (Client)</option>
                                        <option value="system">Système (Super Admin)</option>
                                    </Input>
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Niveau de Hiérarchie</Label>
                                    <Input
                                        name="level"
                                        type="number"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.level || 0}
                                    />
                                    <small className="text-muted">Le niveau détermine qui peut gérer qui (ex: 100 pour Admin, 10 pour Agent)</small>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Description</Label>
                                    <Input
                                        name="description"
                                        type="textarea"
                                        rows="1"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.description || ""}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <hr />
                        <CardTitle className="h5 mb-3">Permissions</CardTitle>

                        <div className="mb-4">
                            {['system', 'company'].map((type) => (
                                <div key={type} className="mb-5">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className={`avatar-xs me-2`}>
                                            <span className={`avatar-title rounded-circle bg-soft-${type === 'system' ? 'danger' : 'primary'} text-${type === 'system' ? 'danger' : 'primary'} font-size-18`}>
                                                <i className={type === 'system' ? "mdi mdi-cog-outline" : "mdi mdi-briefcase-outline"}></i>
                                            </span>
                                        </div>
                                        <h5 className="font-size-16 mb-0">
                                            {type === 'system' ? 'Permissions Système (Super Admin)' : 'Permissions Entreprise (Client)'}
                                        </h5>
                                    </div>
                                    <Row>
                                        {Object.keys(props.allPermissions?.[type] || {}).map((module, mKey) => (
                                            <Col md={4} key={mKey} className="mb-4">
                                                <Card className="border shadow-none mb-0 h-100">
                                                    <CardBody className="p-3">
                                                        <h6 className="font-size-14 text-primary text-uppercase mb-3 border-bottom pb-2">{module}</h6>
                                                        {props.allPermissions[type][module].map((permission, pKey) => (
                                                            <div key={pKey} className="form-check mb-2">
                                                                <Input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id={`perm-${permission.id}`}
                                                                    checked={validation.values.permissions.includes(permission.id)}
                                                                    onChange={() => handlePermissionChange(permission.id)}
                                                                />
                                                                <Label
                                                                    className="form-check-label ms-1"
                                                                    htmlFor={`perm-${permission.id}`}
                                                                >
                                                                    {permission.name}
                                                                </Label>
                                                                <div className="text-muted font-size-11 ms-0" style={{ lineHeight: '1.2' }}>
                                                                    {permission.description}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            ))}
                        </div>

                        <div className="text-end mt-4">
                            <Button type="button" color="secondary" className="me-2" onClick={toggle}>Annuler</Button>
                            <Button type="submit" color="success">
                                {isEdit ? "Mettre à jour" : "Créer le Rôle"}
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>
        </React.Fragment>
    )
}

const mapStateToProps = state => {
    return {
        roles: state.Roles.roles,
        allPermissions: state.Roles.permissions,
    }
}

export default connect(mapStateToProps, {
    setBreadcrumbItems,
    getRoles,
    addNewRole,
    updateRole,
    deleteRole,
    getPermissions
})(RolesList)
